/**
 * UI adapter: bridges React tick scheduler to AdvanceResearchUseCase (UC-05)
 * Enable via architectureFeatures.useAdvanceResearchUseCase
 */
import { useCallback } from 'react';
import { TECH_TREE } from '../../../../constants';
import type { ResearchState } from '../../../../types';
import { advanceResearchUseCase } from '../../../infrastructure/di/container';
import { architectureFeatures } from '../../../infrastructure/config/features';
import type { AdvanceResearchOutput } from '../../../application/dto/AdvanceResearchDto';

/** Run research advancement through clean architecture use case */
export function executeAdvanceResearch(research: ResearchState): AdvanceResearchOutput {
  if (!architectureFeatures.useAdvanceResearchUseCase) {
    throw new Error('useAdvanceResearchUseCase feature flag is disabled');
  }

  return advanceResearchUseCase.execute({ research });
}

/** Legacy fallback — inline research tick logic (migration helper) */
export function executeLegacyAdvanceResearch(research: ResearchState): AdvanceResearchOutput {
  if (!research.activeResearchId) {
    return { research };
  }

  const tech = TECH_TREE.find((t) => t.id === research.activeResearchId);
  if (!tech) {
    return { research };
  }

  const newProgress = research.researchProgress + 1 / tech.duration;

  if (newProgress >= 1) {
    const levelBoost = tech.effects?.maxLevelIncrease || 0;
    return {
      research: {
        ...research,
        activeResearchId: null,
        researchProgress: 0,
        completedTechs: [...research.completedTechs, tech.id],
        unlockedBuildings: [...research.unlockedBuildings, ...tech.unlocks],
        currentMaxLevel: research.currentMaxLevel + levelBoost,
      },
      completedTechId: tech.id,
      newsMessage: `R&D COMPLETE: ${tech.name} unlocked.`,
    };
  }

  return {
    research: {
      ...research,
      researchProgress: newProgress,
    },
  };
}

/**
 * Hook helper for App.tsx integration.
 * Returns a stable callback that runs UC-05 when feature flag is on.
 */
export function useAdvanceResearchRunner() {
  return useCallback((research: ResearchState): AdvanceResearchOutput => {
    if (architectureFeatures.useAdvanceResearchUseCase) {
      return executeAdvanceResearch(research);
    }
    return executeLegacyAdvanceResearch(research);
  }, []);
}